import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { PageShell, redButton, goldButton } from '../components/PageShell';
import { useAuth } from '../context/AuthContext';
import { formatApiError, orderApi } from '../lib/orderApi';
import PrivacyPolicy from '../components/PrivacyPolicy';
import TermsOfService from '../components/TermsOfService';

const legalLink = { background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', padding: 0, fontSize: 'inherit', textDecoration: 'underline' };

const inputStyle = {
  width: '100%', background: '#141414', border: '1px solid rgba(201,168,76,0.25)',
  color: '#F5F5F0', padding: '13px 15px', fontSize: '14px', outline: 'none',
};

const AuthPage = ({ mode }) => {
  const isRegister = mode === 'register';
  const { login, register, setToken } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get('next') || '/pedir';

  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', marketing_opt_in: false, accept_terms: false });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  
  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tfaSessionId, setTfaSessionId] = useState('');
  const [tfaCode, setTfaCode] = useState('');
  const [resending, setResending] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const googleAuth = () => {
    localStorage.setItem('yamauchi_auth_next', next);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/auth/callback')}`;
  };

  let submitLabel = isRegister ? 'Crear cuenta' : 'Entrar';
  if (busy) submitLabel = 'Procesando…';


  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (isRegister && !form.accept_terms) {
      setError('Debes aceptar los Términos de Uso y la Política de Privacidad para crear tu cuenta.');
      return;
    }
    setBusy(true);
    try {
      if (isRegister) {
        await register(form);
        navigate(next);
      } else {
        // Try login - may require 2FA for admin
        const { data } = await orderApi.post('/auth/login', { email: form.email, password: form.password });
        
        if (data.requires_2fa) {
          // Admin needs 2FA verification
          setNeeds2FA(true);
          setTfaSessionId(data.session_id);
          setError('');
        } else {
          // Normal login - cookie set by server, refresh user state
          await setToken();
          navigate(next);
        }
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const verify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await orderApi.post('/auth/verify-2fa', {
        session_id: tfaSessionId,
        code: tfaCode.trim(),
      });
      // Cookie set by server, refresh user state
      await setToken();
      navigate(next);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resend2FA = async () => {
    setResending(true);
    setError('');
    try {
      await orderApi.post('/auth/resend-2fa', { session_id: tfaSessionId });
      setError(''); // Clear any previous error
      // Show success briefly
      setError('✓ Código reenviado');
      setTimeout(() => setError(''), 3000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setResending(false);
    }
  };

  // 2FA verification screen
  if (needs2FA) {
    return (
      <PageShell>
        <div className="mx-auto" style={{ maxWidth: '440px' }}>
          <button
            type="button"
            onClick={() => { setNeeds2FA(false); setTfaCode(''); setError(''); }}
            className="font-body inline-flex items-center gap-2 mb-6"
            style={{ fontSize: '13px', color: '#C9A84C', background: 'none', border: 'none', cursor: 'pointer' }}
            data-testid="2fa-back"
          >
            <span style={{ fontSize: '16px' }}>←</span> Volver al login
          </button>
          
          <h1 className="font-display" style={{ fontSize: '38px', fontWeight: 700, color: '#F5F5F0' }}>
            Verificación
          </h1>
          <p className="font-body mt-2" style={{ fontSize: '14px', fontWeight: 300, color: '#C2C1B6' }}>
            Enviamos un código de 6 dígitos a tu email. Ingrésalo para continuar.
          </p>

          <form onSubmit={verify2FA} className="mt-8 space-y-4">
            <input
              style={{ ...inputStyle, textAlign: 'center', letterSpacing: '8px', fontSize: '24px', fontFamily: 'monospace' }}
              type="text"
              placeholder="000000"
              value={tfaCode}
              onChange={(e) => setTfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              required
              autoFocus
              data-testid="2fa-code"
            />

            {error && (
              <p className="font-body" style={{ fontSize: '13px', color: error.startsWith('✓') ? '#7FB77E' : '#E0736F' }} data-testid="2fa-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy || tfaCode.length !== 6}
              style={{ ...redButton, width: '100%', opacity: busy || tfaCode.length !== 6 ? 0.6 : 1 }}
              data-testid="2fa-submit"
            >
              {busy ? 'Verificando…' : 'Verificar código'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={resend2FA}
              disabled={resending}
              className="font-body"
              style={{ fontSize: '13px', color: '#C9A84C', background: 'none', border: 'none', cursor: 'pointer', opacity: resending ? 0.6 : 1 }}
              data-testid="2fa-resend"
            >
              {resending ? 'Enviando…' : '¿No recibiste el código? Reenviar'}
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="mx-auto" style={{ maxWidth: '440px' }}>
        <button
          type="button"
          onClick={() => navigate(next)}
          className="font-body inline-flex items-center gap-2 mb-6"
          style={{ fontSize: '13px', color: '#C9A84C', background: 'none', border: 'none', cursor: 'pointer' }}
          data-testid="auth-back"
        >
          <span style={{ fontSize: '16px' }}>←</span> Volver
        </button>
        <h1 className="font-display" style={{ fontSize: '38px', fontWeight: 700, color: '#F5F5F0' }}>
          {isRegister ? 'Crear cuenta' : 'Entrar'}
        </h1>
        <p className="font-body mt-2" style={{ fontSize: '14px', fontWeight: 300, color: '#C2C1B6' }}>
          {isRegister ? 'Regístrate para ordenar tu delivery de los viernes.' : 'Accede para hacer y seguir tus pedidos.'}
        </p>

        <button
          type="button"
          onClick={googleAuth}
          className="mt-8 w-full flex items-center justify-center gap-3"
          style={{ background: '#F5F5F0', color: '#1A1A1A', padding: '13px', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
          data-testid="google-auth-btn"
        >
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          Continuar con Google
        </button>
        {isRegister && (
          <p className="font-body mt-2" style={{ fontSize: '11px', color: '#8A897E', lineHeight: 1.5 }}>
            Al continuar con Google aceptas los <button type="button" onClick={() => setShowTerms(true)} style={legalLink}>Términos de Uso</button> y la <button type="button" onClick={() => setShowPrivacy(true)} style={legalLink}>Política de Privacidad</button>.
          </p>
        )}

        <div className="flex items-center gap-3 mt-6">
          <span style={{ flex: 1, height: '1px', background: 'rgba(201,168,76,0.2)' }} />
          <span className="font-body uppercase" style={{ fontSize: '10px', letterSpacing: '2px', color: '#C2C1B6' }}>o con tu email</span>
          <span style={{ flex: 1, height: '1px', background: 'rgba(201,168,76,0.2)' }} />
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {isRegister && (
            <input style={inputStyle} placeholder="Nombre completo" value={form.name} onChange={change('name')} required data-testid="auth-name" />
          )}          <input style={inputStyle} type="email" placeholder="Email" value={form.email} onChange={change('email')} required data-testid="auth-email" />
          {isRegister && (
            <input style={inputStyle} placeholder="Teléfono" value={form.phone} onChange={change('phone')} required data-testid="auth-phone" />
          )}
          <input style={inputStyle} type="password" placeholder="Contraseña" value={form.password} onChange={change('password')} required data-testid="auth-password" />

          {isRegister && (
            <label className="flex items-center gap-2 cursor-pointer" data-testid="auth-marketing-label">
              <input type="checkbox" checked={form.marketing_opt_in} onChange={(e) => setForm({ ...form, marketing_opt_in: e.target.checked })} style={{ accentColor: '#C9A84C', width: '15px', height: '15px' }} data-testid="auth-marketing" />
              <span className="font-body" style={{ fontSize: '12px', color: '#C2C1B6' }}>Quiero recibir ofertas y novedades por email</span>
            </label>
          )}

          {isRegister && (
            <label className="flex items-start gap-2 cursor-pointer" data-testid="auth-terms-label">
              <input type="checkbox" checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })} style={{ accentColor: '#C9A84C', width: '15px', height: '15px', marginTop: '2px' }} data-testid="auth-terms" />
              <span className="font-body" style={{ fontSize: '12px', color: '#C2C1B6', lineHeight: 1.5 }}>
                Acepto los <button type="button" onClick={() => setShowTerms(true)} style={legalLink} data-testid="auth-terms-link">Términos de Uso</button> y la <button type="button" onClick={() => setShowPrivacy(true)} style={legalLink} data-testid="auth-privacy-link">Política de Privacidad</button>
              </span>
            </label>
          )}

          {error && (
            <p className="font-body" style={{ fontSize: '13px', color: '#E0736F' }} data-testid="auth-error">{error}</p>
          )}

          <button type="submit" disabled={busy} style={{ ...redButton, width: '100%', opacity: busy ? 0.6 : 1 }} data-testid="auth-submit">
            {submitLabel}
          </button>
        </form>

        <p className="font-body mt-6" style={{ fontSize: '13px', color: '#C2C1B6' }}>
          {isRegister ? '¿Ya tienes cuenta? ' : '¿No tienes cuenta? '}
          <Link to={isRegister ? `/login?next=${encodeURIComponent(next)}` : `/register?next=${encodeURIComponent(next)}`} style={{ color: '#C9A84C' }} data-testid="auth-toggle">
            {isRegister ? 'Entrar' : 'Crear cuenta'}
          </Link>
        </p>
      </div>
      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
    </PageShell>
  );
};

export default AuthPage;
